/**
 * pi-featured-collection-slider – Custom element <pi-featured-collection-slider> + Swiper 11.
 * Optional mobile CSS grid: when mobileGrid is true, Swiper is only active from layout breakpoint up
 * so it does not fight the static grid below that width.
 */
(function () {
  'use strict';

  function parseConfig(root) {
    var scriptEl = root.querySelector('script[type="application/json"][data-pi-swiper-config]');
    if (!scriptEl || !scriptEl.textContent) return null;
    try {
      return JSON.parse(scriptEl.textContent.trim());
    } catch (e) {
      return null;
    }
  }

  function buildOptions(root, config) {
    var bp = parseInt(config.breakpoint, 10) || 990;
    var slidesPerView = config.slidesPerView != null ? Number(config.slidesPerView) : 1.15;
    var spaceBetween = parseInt(config.spaceBetween, 10) || 0;
    var spvDesk = config.slidesPerViewDesktop != null ? Number(config.slidesPerViewDesktop) : 4;
    var spaceDesk = parseInt(config.spaceBetweenDesktop, 10) || 16;

    if (isNaN(slidesPerView) || slidesPerView <= 0) slidesPerView = 1.15;
    if (isNaN(spvDesk) || spvDesk <= 0) spvDesk = 4;

    var opts = {
      slidesPerView: slidesPerView,
      spaceBetween: spaceBetween,
      watchOverflow: true,
      watchSlidesProgress: true,
      grabCursor: !!config.grabCursor,
      centeredSlides: !!config.centeredSlidesMobile,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      breakpoints: {}
    };

    if (config.freeMode) {
      opts.freeMode = true;
    }

    opts.breakpoints[bp] = {
      slidesPerView: spvDesk,
      spaceBetween: spaceDesk,
      centeredSlides: !!config.centeredSlidesDesktop
    };

    if (config.loop) {
      opts.loop = true;
      opts.loopAdditionalSlides = 2;
    }

    if (config.autoplay) {
      opts.autoplay = {
        delay: parseInt(config.autoplayDelay, 10) || 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      };
    }

    var prevEl = root.querySelector('.swiper-button-prev');
    var nextEl = root.querySelector('.swiper-button-next');
    if (prevEl && nextEl && (config.navMobile || config.navDesktop)) {
      opts.navigation = { prevEl: prevEl, nextEl: nextEl };
    }

    var pagEl = root.querySelector('.pi-featured-collection__swiper-pagination');
    if (pagEl && (config.paginationMobile || config.paginationDesktop)) {
      var pType = config.paginationType || 'bullets';
      opts.pagination = {
        el: pagEl,
        clickable: true
      };
      if (pType === 'fraction' || pType === 'progressbar') {
        opts.pagination.type = pType;
      } else {
        opts.pagination.type = 'bullets';
        if (config.paginationDynamicBullets) {
          opts.pagination.dynamicBullets = true;
        }
      }
    }

    opts.on = {
      init: function (swiper) {
        syncLockedClass(swiper);
      },
      lock: function (swiper) {
        syncLockedClass(swiper);
      },
      unlock: function (swiper) {
        syncLockedClass(swiper);
      },
      resize: function (swiper) {
        syncLockedClass(swiper);
      },
      breakpoint: function (swiper) {
        syncLockedClass(swiper);
      }
    };

    return opts;
  }

  function syncLockedClass(swiper) {
    if (!swiper || !swiper.el) return;
    var locked = swiper.isLocked === true;
    swiper.el.classList.toggle('pi-fc-swiper--locked', locked);
  }

  function shouldUseSwiper(config) {
    if (!config || !config.mobileGrid) return true;
    var bp = parseInt(config.breakpoint, 10) || 990;
    return window.matchMedia('(min-width: ' + bp + 'px)').matches;
  }

  class PIFeaturedCollectionSlider extends HTMLElement {
    connectedCallback() {
      if (typeof window.Swiper === 'undefined') {
        this._piRetry = (this._piRetry || 0) + 1;
        if (this._piRetry < 50) {
          setTimeout(
            function () {
              if (this.isConnected) this.connectedCallback();
            }.bind(this),
            50
          );
        }
        return;
      }

      var config = parseConfig(this);
      if (!config) return;

      this._piConfig = config;
      if (!this._piListenersBound) {
        this._piListenersBound = true;
        this._onViewportChange = this._onViewportChange.bind(this);
        window.addEventListener('resize', this._onViewportChange);
        if (window.matchMedia) {
          var bpMq = parseInt(config.breakpoint, 10) || 990;
          this._piMq = window.matchMedia('(min-width: ' + bpMq + 'px)');
          if (this._piMq.addEventListener) {
            this._piMq.addEventListener('change', this._onViewportChange);
          } else if (this._piMq.addListener) {
            this._piMq.addListener(this._onViewportChange);
          }
        }
      }

      this._applySwiperState();
    }

    _onViewportChange() {
      if (this._piResizeTimer) clearTimeout(this._piResizeTimer);
      var self = this;
      this._piResizeTimer = setTimeout(function () {
        self._applySwiperState();
      }, 120);
    }

    _applySwiperState() {
      var config = this._piConfig;
      if (!config) return;

      var swiperEl = this.querySelector('.pi-featured-collection__swiper.swiper');
      if (!swiperEl) return;

      var useSwiper = shouldUseSwiper(config);

      if (!useSwiper) {
        if (this._swiper && typeof this._swiper.destroy === 'function') {
          try {
            this._swiper.destroy(true, true);
          } catch (e) {}
          this._swiper = null;
        }
        this._piSwiperInited = false;
        this.setAttribute('data-pi-swiper-mode', 'mobile-grid');
        return;
      }

      this.removeAttribute('data-pi-swiper-mode');

      if (this._piSwiperInited && this._swiper) {
        if (typeof this._swiper.update === 'function') {
          this._swiper.update();
          syncLockedClass(this._swiper);
        }
        return;
      }

      var opts = buildOptions(this, config);
      this._swiper = new window.Swiper(swiperEl, opts);
      this._piSwiperInited = true;

      var self = this;
      requestAnimationFrame(function () {
        if (self._swiper && typeof self._swiper.update === 'function') {
          self._swiper.update();
          syncLockedClass(self._swiper);
        }
      });
      setTimeout(function () {
        if (self._swiper && typeof self._swiper.update === 'function') {
          self._swiper.update();
          syncLockedClass(self._swiper);
        }
      }, 150);
    }

    disconnectedCallback() {
      if (this._piListenersBound) {
        window.removeEventListener('resize', this._onViewportChange);
        if (this._piMq) {
          if (this._piMq.removeEventListener) {
            this._piMq.removeEventListener('change', this._onViewportChange);
          } else if (this._piMq.removeListener) {
            this._piMq.removeListener(this._onViewportChange);
          }
          this._piMq = null;
        }
        this._piListenersBound = false;
      }
      if (this._piResizeTimer) clearTimeout(this._piResizeTimer);

      if (this._swiper && typeof this._swiper.destroy === 'function') {
        try {
          this._swiper.destroy(true, true);
        } catch (e) {}
        this._swiper = null;
      }
      this._piSwiperInited = false;
      this._piConfig = null;
      this.removeAttribute('data-pi-swiper-mode');
    }
  }

  if (!customElements.get('pi-featured-collection-slider')) {
    customElements.define('pi-featured-collection-slider', PIFeaturedCollectionSlider);
  }
})();
